import {
  Autocomplete,
  AutocompleteOption,
  Icons,
  ListItemContent,
  Stack,
  Typography,
  VariantProp,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCreateAddress } from '../../../api/created/address.ts';
import {
  getGetAddressesQueryKey,
  useGetAddresses,
} from '../../../api/generated/address-controller/address-controller.ts';
import { AddressContactDto, AddressDto, AddressDtoAddressType } from '../../../api/generated/model';
import { syncAddress } from '../../../api/generated/sync-controller/address-sync-controller.ts';
import { useSyncableSelection } from '../../../hooks/useSyncableSelection.ts';
import { getAddressTypeName } from '../../../utils/address.ts';
import { CreateAddressDialog } from './CreateAddressDialog.tsx';
import { AddressDisplay } from './PatientAddressDisplay.tsx';

interface Props {
  onChange: (addressDto: AddressDto | null) => void;
  value: AddressDto | null;
  addressTypes: AddressDtoAddressType[];
  canChangeAddressType: boolean;
  patientId?: string;
  contactId?: string;
  variant?: VariantProp;
  showSyncIcon?: boolean;
  disabled?: boolean;
  onAutoSelectFromUniqueMatch?: (address: AddressDto) => void;
  /**
   * The Ansprechperson as it stands after an Institution sync. Syncing the address reconciles the
   * pair as well, so it can empty a contact that no longer belongs to this Institution — the
   * contact field has to hear about it even though the user pressed the address's sync button.
   */
  onContactSynced?: (contactDto: AddressContactDto | null) => void;
}

const CREATE_ADDRESS_OPTION = {
  addressType: AddressDtoAddressType.WELFARE_AGENCY,
  address: 'Institution erstellen',
} as AddressDto;
const MAX_VISIBLE_ADDRESS_OPTIONS = 5;
const AUTOCOMPLETE_INPUT_SX = {
  '&.Mui-error:not(.Mui-disabled) .MuiAutocomplete-input': {
    color: 'text.primary',
  },
};

function formatAddressOptionLabel(address: AddressDto) {
  return [address.organization, address.address, [address.zip, address.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

function isSameAddress(option: AddressDto, selected: AddressDto) {
  if (option.id && selected.id) {
    return option.id === selected.id;
  }

  return (
    option.address === selected.address &&
    option.organization === selected.organization &&
    option.city === selected.city &&
    option.addressType === selected.addressType &&
    option.phone === selected.phone
  );
}

function splitOptionProps<TProps extends { key: React.Key }>(props: TProps) {
  const { key, ...optionProps } = props;
  return { key, optionProps };
}

export function SelectAddress({
  addressTypes,
  canChangeAddressType,
  patientId,
  contactId,
  onChange,
  value,
  variant,
  showSyncIcon = false,
  disabled = false,
  onAutoSelectFromUniqueMatch,
  onContactSynced,
}: Props) {
  const queryClient = useQueryClient();
  const addressQueryParams = {
    ...(patientId ? { patientId } : {}),
    ...(contactId ? { contactId } : {}),
  };
  const { data: addresses, isPending } = useGetAddresses(addressQueryParams);
  const [isCreateNewAddressDialogOpen, setIsCreateNewAddressDialogOpen] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [newAddress, setNewAddress] = useState<AddressDto | null>(null);
  const lastAutoSelectContactIdRef = useRef<string | undefined>(undefined);

  const createAddressMutation = useCreateAddress({
    mutation: {
      onError: () => {
        toast.error('Institution konnte nicht erstellt werden');
      },
    },
  });

  const transformOptions = (options: AddressDto[]): (AddressDto & { addressTypeName: string })[] => {
    return options.map((option) => ({
      ...option,
      addressTypeName: getAddressTypeName(option?.addressType as AddressDtoAddressType),
    }));
  };

  const filterAddressOptions = (options: AddressDto[], { inputValue }: { inputValue: string }) =>
    matchSorter(transformOptions(options), inputValue, {
      keys: ['organization', 'address', 'zip', 'city', 'addressTypeName'],
    });

  const addressList = addresses ?? [];
  const {
    availableOptions: resolvedOptions,
    selectedValue: selectedAddress,
    triggerSync,
    isSyncing,
  } = useSyncableSelection({
    value,
    options: addressList,
    pendingOption: newAddress,
    isSame: isSameAddress,
    sync: patientId
      ? {
          mutationFn: ({ patientId, referenceType }: { patientId: string; referenceType: string }) =>
            syncAddress(patientId, referenceType),
          invalidateQueryKey: getGetAddressesQueryKey(addressQueryParams),
          onSynced: (patient) => onContactSynced?.(patient.address?.contact ?? null),
          successMessage: 'Adresse erfolgreich synchronisiert',
          errorMessage: 'Synchronisierung fehlgeschlagen',
        }
      : undefined,
  });

  useEffect(() => {
    if (
      addresses &&
      newAddress &&
      addresses.some(
        (address) =>
          address.address === newAddress.address &&
          address.organization === newAddress.organization &&
          address.city === newAddress.city &&
          address.addressType === newAddress.addressType &&
          address.phone === newAddress.phone,
      )
    ) {
      setNewAddress(null);
    }
  }, [addresses, newAddress]);

  useEffect(() => {
    if (!contactId) {
      lastAutoSelectContactIdRef.current = undefined;
      return;
    }
    if (value) return;
    if (lastAutoSelectContactIdRef.current === contactId) return;
    if (!onAutoSelectFromUniqueMatch) return;
    if (addressList.length === 1) {
      lastAutoSelectContactIdRef.current = contactId;
      onAutoSelectFromUniqueMatch(addressList[0]);
    }
  }, [contactId, addressList, value, onAutoSelectFromUniqueMatch]);

  return (
    <>
      <Stack gap={1}>
        <Autocomplete
          value={selectedAddress}
          disabled={disabled}
          loading={isPending || createAddressMutation.isPending}
          options={resolvedOptions}
          variant={variant}
          open={isAutocompleteOpen}
          onOpen={() => setIsAutocompleteOpen(true)}
          onClose={() => setIsAutocompleteOpen(false)}
          openOnFocus
          sx={AUTOCOMPLETE_INPUT_SX}
          getOptionLabel={formatAddressOptionLabel}
          isOptionEqualToValue={(option, selectedValue) => isSameAddress(option, selectedValue)}
          onChange={(_, selectedValue) => {
            if (selectedValue?.address === CREATE_ADDRESS_OPTION.address) {
              setIsAutocompleteOpen(false);
              (document.activeElement as HTMLElement | null)?.blur();
              setIsCreateNewAddressDialogOpen(true);
              return;
            }

            onChange(selectedValue);
          }}
          filterOptions={(options, state) => {
            if (!state.inputValue.trim()) {
              return contactId
                ? options.slice(0, MAX_VISIBLE_ADDRESS_OPTIONS)
                : [CREATE_ADDRESS_OPTION, ...options.slice(0, MAX_VISIBLE_ADDRESS_OPTIONS)];
            }

            const filtered = filterAddressOptions(options, state).map(
              ({ addressTypeName: _addressTypeName, ...rest }) => rest,
            );

            return contactId
              ? filtered.slice(0, MAX_VISIBLE_ADDRESS_OPTIONS)
              : [CREATE_ADDRESS_OPTION, ...filtered.slice(0, MAX_VISIBLE_ADDRESS_OPTIONS)];
          }}
          renderOption={(props, option) => {
            const { key, optionProps } = splitOptionProps(props as typeof props & { key: React.Key });

            if (option.address !== CREATE_ADDRESS_OPTION.address) {
              return (
                <AutocompleteOption key={key} {...optionProps}>
                  <ListItemContent>
                    <Typography level="title-sm">{option.organization}</Typography>

                    <Typography level="body-sm">
                      {option.address}
                      {option.address && ','} {option.zip} {option.city}
                    </Typography>
                  </ListItemContent>
                </AutocompleteOption>
              );
            }

            return (
              <AutocompleteOption key={key} {...optionProps}>
                <ListItemContent>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Icons.Add />
                    <Typography level="title-sm">Neue Institution erstellen</Typography>
                  </Stack>
                </ListItemContent>
              </AutocompleteOption>
            );
          }}
        />

        {selectedAddress && (
          <div style={{ paddingLeft: 10 }}>
            <AddressDisplay
              address={selectedAddress}
              showSyncIcon={showSyncIcon && !!patientId}
              onSyncClick={() => {
                if (!patientId) return;
                triggerSync({
                  patientId,
                  referenceType: 'PATIENT_RESIDENCE',
                });
              }}
              syncLoading={isSyncing}
            />
          </div>
        )}
      </Stack>

      <CreateAddressDialog
        open={isCreateNewAddressDialogOpen}
        onClose={(addressDto) => {
          setIsCreateNewAddressDialogOpen(false);

          if (addressDto) {
            void (async () => {
              try {
                const addressId = await createAddressMutation.mutateAsync({ data: addressDto });
                const createdAddress = { ...addressDto, id: addressId };

                setNewAddress(createdAddress);
                onChange(createdAddress);

                await queryClient.invalidateQueries({
                  queryKey: getGetAddressesQueryKey(addressQueryParams),
                });
              } catch {
                // Error toast is already handled by the mutation.
              }
            })();
          }
        }}
        selectedAddressTypes={addressTypes}
        canChangeAddressType={canChangeAddressType}
      />
    </>
  );
}
